import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import SEO from './SEO';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_c07ebe4f-8fac-4375-bd77-db80e53251b9/artifacts/3hq7a21m_flinch-logo-primary-dark.png";

const FranchiseChatbotPage = () => {
  const { isDarkMode } = useTheme();

  // Load Odin chatbot script
  useEffect(() => {
    // Set global config for Odin
    window.project_id = "e28b3278fd02409bb7d245";
    window.frame_type = "window";

    // Check if script already exists
    const existingScript = document.querySelector('script[src="https://app.getodin.ai/loader.min.js"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = "https://app.getodin.ai/loader.min.js";
      script.setAttribute('project_id', 'e28b3278fd02409bb7d245');
      script.setAttribute('frame_type', 'window');
      script.defer = true;
      document.body.appendChild(script);

      return () => {
        // Cleanup on unmount
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F2F2F2] relative overflow-hidden">
      <SEO 
        title="Franchise AI Assistant | FLINCH"
        description="Boost franchise efficiency with 24/7 AI support. Instant answers for franchisees, consistent brand messaging, zero wait time. Scale smarter."
        canonical="/franchise-chatbot"
      />

      {/* Abstract Background Elements */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* Large yellow gradient blob - top right */}
        <div 
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full blur-[150px] opacity-20"
          style={{ background: 'radial-gradient(circle, #F6CD17 0%, transparent 70%)' }}
        />
        
        {/* Subtle white gradient - bottom left */}
        <div 
          className="absolute -bottom-60 -left-60 w-[500px] h-[500px] rounded-full blur-[120px] opacity-5"
          style={{ background: 'radial-gradient(circle, #FFFFFF 0%, transparent 70%)' }}
        />

        {/* Geometric lines */}
        <svg className="absolute top-0 left-0 w-full h-full opacity-[0.03]" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#F6CD17" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        {/* Floating abstract shapes */}
        <div className="absolute top-1/4 left-10 w-2 h-32 bg-[#F6CD17]/20 transform rotate-12" />
        <div className="absolute top-1/3 right-20 w-1 h-24 bg-white/10 transform -rotate-45" />
        <div className="absolute bottom-1/4 left-1/4 w-3 h-3 bg-[#F6CD17]/40 rounded-full" />
        <div className="absolute top-1/2 right-1/3 w-2 h-2 bg-white/20 rounded-full" />
      </div>

      {/* Minimal Header */}
      <header className="relative z-10 flex justify-between items-center p-6 md:p-8">
        <Link to="/" className="transition-opacity duration-300 hover:opacity-80">
          <img 
            src={LOGO_URL} 
            alt="FLINCH Logo - Franchise AI Solutions" 
            className="h-6 md:h-8 w-auto brightness-0 invert"
          />
        </Link>
        
        <Link 
          to="/"
          className="text-sm text-[#999999] hover:text-[#F2F2F2] transition-colors duration-300 flex items-center gap-2"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M13 8H3M3 8L7 4M3 8L7 12" />
          </svg>
          Back to FLINCH
        </Link>
      </header>

      {/* Main Content */}
      <main className="relative z-10 flex flex-col items-center justify-center min-h-[calc(100vh-200px)] px-6 py-12">
        
        {/* Headline Section */}
        <div className="text-center mb-12 max-w-3xl">
          <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#F6CD17] mb-6 font-medium">
            Franchise Intelligence
          </span>
          <h1 className="headline-xl text-4xl sm:text-5xl md:text-6xl lg:text-7xl mb-6 leading-[0.95]">
            <span className="text-white">Your Network.</span>
            <br />
            <span className="text-[#F6CD17]">Always On.</span>
          </h1>
          <p className="text-[#999999] text-lg md:text-xl max-w-xl mx-auto leading-relaxed">
            Instant answers for franchisees. Consistent messaging. Zero wait time.
          </p>
        </div>

        {/* Chatbot Container */}
        <div className="w-full max-w-2xl mx-auto">
          {/* Chatbot Wrapper with styling */}
          <div 
            className="relative rounded-2xl overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(246, 205, 23, 0.1) 0%, rgba(255,255,255,0.02) 50%, rgba(246, 205, 23, 0.05) 100%)',
              border: '1px solid rgba(246, 205, 23, 0.2)',
              boxShadow: '0 0 80px rgba(246, 205, 23, 0.1), 0 25px 50px -12px rgba(0, 0, 0, 0.5)',
              minHeight: '500px',
            }}
          >
            {/* Glowing corners */}
            <div className="absolute top-0 left-0 w-20 h-20 bg-[#F6CD17]/10 blur-2xl" />
            <div className="absolute bottom-0 right-0 w-20 h-20 bg-[#F6CD17]/10 blur-2xl" />
            
            {/* Odin Chatbot Embed */}
            <div 
              id="odin-chatbot" 
              className="relative z-10 w-full"
              data-testid="odin-chatbot-container"
            />
          </div>

          {/* Subtle branding below chatbot */}
          <div className="mt-8 text-center">
            <p className="text-[#555555] text-xs tracking-wider">
              Powered by <span className="text-[#F6CD17]">FLINCH</span> × Odin AI
            </p>
          </div>
        </div>

        {/* Feature Pills */}
        <div className="flex flex-wrap justify-center gap-3 mt-16">
          {['24/7 Availability', 'Instant Responses', 'Brand Consistent', 'Always Learning'].map((feature, index) => (
            <span 
              key={feature}
              className="px-4 py-2 text-xs uppercase tracking-wider rounded-full border border-white/10 text-[#999999] bg-white/[0.02]"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {feature}
            </span>
          ))}
        </div>

        {/* Core Benefits Section */}
        <section className="mt-24 w-full max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#F6CD17] mb-4 font-medium">
              Why It Matters
            </span>
            <h2 className="headline-xl text-2xl sm:text-3xl md:text-4xl text-white mb-4">
              Core Benefits
            </h2>
            <p className="text-[#999999] max-w-2xl mx-auto">
              Transform your franchise operations with AI-powered efficiency that delivers measurable ROI.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'Reduce Support Costs',
                description: 'Cut franchisee support overhead by up to 60% with automated responses to common questions. Your team focuses on high-value strategic work.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
                  </svg>
                )
              },
              {
                title: 'Scale Without Hiring',
                description: 'Support 100 or 1,000 franchisees with the same system. AI handles volume spikes during launches, promotions, and seasonal peaks automatically.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
                  </svg>
                )
              },
              {
                title: 'Ensure Brand Consistency',
                description: 'Every franchisee receives the same accurate, on-brand information. Eliminate miscommunication and protect your brand standards across all locations.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                )
              }
            ].map((benefit, index) => (
              <div 
                key={benefit.title}
                className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.05] transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-[#F6CD17]/10 flex items-center justify-center text-[#F6CD17] mb-4">
                  {benefit.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-[#999999] text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* How It Works Section */}
        <section className="mt-24 w-full max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#F6CD17] mb-4 font-medium">
              Simple Implementation
            </span>
            <h2 className="headline-xl text-2xl sm:text-3xl md:text-4xl text-white mb-4">
              How It Works
            </h2>
            <p className="text-[#999999] max-w-2xl mx-auto">
              Get your franchise AI assistant live in days, not months. Our streamlined process ensures rapid deployment with immediate results.
            </p>
          </div>
          
          <div className="relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-12 left-[16.67%] right-[16.67%] h-0.5 bg-gradient-to-r from-[#F6CD17]/20 via-[#F6CD17]/40 to-[#F6CD17]/20" />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: '01',
                  title: 'Knowledge Upload',
                  description: 'Share your franchise operations manual, FAQs, brand guidelines, and training materials. Our AI ingests and organizes your institutional knowledge.'
                },
                {
                  step: '02',
                  title: 'Custom Training',
                  description: 'We fine-tune the AI to understand your specific terminology, policies, and brand voice. The system learns your unique franchise ecosystem.'
                },
                {
                  step: '03',
                  title: 'Deploy & Optimize',
                  description: 'Launch your AI assistant across all franchise locations. Monitor performance analytics and continuously improve responses based on real interactions.'
                }
              ].map((step, index) => (
                <div key={step.step} className="relative text-center">
                  <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#F6CD17]/10 border-2 border-[#F6CD17]/30 flex items-center justify-center">
                    <span className="text-2xl font-bold text-[#F6CD17]">{step.step}</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{step.title}</h3>
                  <p className="text-[#999999] text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
          
          {/* CTA */}
          <div className="mt-16 text-center">
            <p className="text-[#999999] mb-6">
              Ready to transform your franchise support operations?
            </p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#F6CD17] text-black rounded-full font-medium hover:bg-[#e0b915] transition-all duration-300"
            >
              Schedule a Demo
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" />
              </svg>
            </Link>
          </div>
        </section>
      </main>

      {/* Minimal Footer */}
      <footer className="relative z-10 p-6 md:p-8 text-center">
        <p className="text-[#555555] text-xs">
          A <Link to="/" className="text-[#999999] hover:text-[#F6CD17] transition-colors">FLINCH</Link> product demo
        </p>
      </footer>
    </div>
  );
};

export default FranchiseChatbotPage;
